import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight, X, Loader2, FolderTree, Folder, FolderOpen } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import { enterAdvance } from '@/hooks/useEnterAdvance'

interface Categoria {
  id: number
  nome: string
  parent_id: number | null
  parent_nome?: string | null
  ativo: boolean
  exibir_pdv?: boolean
}

export default function CategoriasPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<null | Partial<Categoria>>(null)
  const [showForm, setShowForm] = useState(false)
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set())

  const { data: cats = [], isLoading } = useQuery<Categoria[]>({
    queryKey: ['categorias-todas'],
    queryFn: async () => (await api.get('/cadastros/categorias')).data,
  })

  const grupos = useMemo(() => cats.filter(c => c.parent_id == null), [cats])
  const subsByParent = useMemo(() => {
    const m: Record<number, Categoria[]> = {}
    cats.filter(c => c.parent_id != null).forEach(c => {
      const k = c.parent_id as number
      m[k] = m[k] || []; m[k].push(c)
    })
    return m
  }, [cats])

  const salvar = useMutation({
    mutationFn: async (c: Partial<Categoria>) => {
      const payload = { nome: c.nome, parent_id: c.parent_id ?? null, ativo: c.ativo ?? true, exibir_pdv: c.exibir_pdv ?? true }
      if (c.id) return (await api.put(`/cadastros/categorias/${c.id}`, payload)).data
      return (await api.post('/cadastros/categorias', payload)).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categorias-todas'] })
      qc.invalidateQueries({ queryKey: ['cadastros-categorias'] })
      setShowForm(false); setEditing(null)
      toast.success('Salvo')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const remover = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/cadastros/categorias/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categorias-todas'] })
      qc.invalidateQueries({ queryKey: ['cadastros-categorias'] })
      toast.success('Removido')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao remover'),
  })

  const toggleGrupo = (id: number) => {
    setExpandidos(prev => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id); else n.add(id)
      return n
    })
  }

  const totalProdutosPorGrupo = (gid: number) => {
    // Não retornado pela API atual; placeholder. Pode evoluir.
    return 0
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-ink-900 flex items-center gap-2">
            <FolderTree size={20} className="text-brand-600" /> Grupos e Subgrupos de Produtos
          </h2>
          <p className="text-sm text-ink-500 mt-0.5">
            Organize seu catálogo em <strong>grupos</strong> (ex: Bebidas) e <strong>subgrupos</strong> (ex: Refrigerantes, Sucos).
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => { setEditing({ parent_id: null, ativo: true }); setShowForm(true) }}>
            <Plus size={14} /> Novo grupo
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading && (
          <div className="text-center py-10 text-ink-400">
            <Loader2 size={20} className="animate-spin inline mr-2" />Carregando...
          </div>
        )}
        {!isLoading && grupos.length === 0 && (
          <div className="text-center py-12 text-ink-400">
            <FolderTree size={36} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Nenhum grupo cadastrado.</p>
            <button className="btn-primary mt-3" onClick={() => { setEditing({ parent_id: null, ativo: true }); setShowForm(true) }}>
              <Plus size={14} /> Criar primeiro grupo
            </button>
          </div>
        )}
        {!isLoading && grupos.length > 0 && (
          <div className="divide-y divide-ink-100">
            {grupos.map(g => {
              const subs = subsByParent[g.id] || []
              const open = expandidos.has(g.id)
              return (
                <div key={g.id}>
                  <div className="flex items-center gap-2 px-4 py-3 hover:bg-ink-50">
                    <button onClick={() => toggleGrupo(g.id)} className="p-1 rounded hover:bg-ink-200" disabled={subs.length === 0}>
                      {subs.length === 0 ? <span className="w-4 inline-block" /> : open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    {open ? <FolderOpen size={16} className="text-amber-600" /> : <Folder size={16} className="text-amber-600" />}
                    <span className="font-medium text-ink-900 flex-1">{g.nome}</span>
                    <span className="text-xs text-ink-500 mr-2">{subs.length} subgrupo{subs.length !== 1 ? 's' : ''}</span>
                    {!g.ativo && <span className="text-[10px] uppercase tracking-wide text-ink-400 mr-2">inativo</span>}
                    <button onClick={() => { setEditing({ id: g.id, nome: g.nome, parent_id: null, ativo: g.ativo, exibir_pdv: g.exibir_pdv ?? true }); setShowForm(true) }}
                      className="p-1.5 rounded hover:bg-brand-50 text-brand-700" title="Editar">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => { setEditing({ parent_id: g.id, ativo: true }); setShowForm(true) }}
                      className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700" title="Adicionar subgrupo">
                      <Plus size={13} />
                    </button>
                    <button
                      onClick={() => pedirExclusao({ descricao: `o grupo "${g.nome}"`, onConfirm: () => remover.mutate(g.id) })}
                      className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Remover">
                      <Trash2 size={13} />
                    </button>
                  </div>
                  {open && subs.length > 0 && (
                    <div className="bg-ink-50/50 border-l-2 border-amber-300 ml-8">
                      {subs.map(s => (
                        <div key={s.id} className="flex items-center gap-2 px-4 py-2 hover:bg-white">
                          <Folder size={13} className="text-sky-600 ml-5" />
                          <span className="text-sm flex-1">{s.nome}</span>
                          {!s.ativo && <span className="text-[10px] uppercase tracking-wide text-ink-400 mr-2">inativo</span>}
                          <button onClick={() => { setEditing({ id: s.id, nome: s.nome, parent_id: s.parent_id, ativo: s.ativo, exibir_pdv: s.exibir_pdv ?? true }); setShowForm(true) }}
                            className="p-1 rounded hover:bg-brand-50 text-brand-700" title="Editar">
                            <Pencil size={12} />
                          </button>
                          <button
                            onClick={() => pedirExclusao({ descricao: `o subgrupo "${s.nome}"`, onConfirm: () => remover.mutate(s.id) })}
                            className="p-1 rounded hover:bg-rose-50 text-rose-600" title="Remover">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {showForm && editing && (
        <div className="fixed inset-0 bg-ink-950/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onKeyDown={enterAdvance}>
            <div className="px-5 py-4 border-b border-ink-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-ink-900">
                {editing.id ? 'Editar' : 'Novo'} {editing.parent_id ? 'subgrupo' : 'grupo'}
              </h3>
              <button onClick={() => { setShowForm(false); setEditing(null) }} className="p-1 rounded hover:bg-ink-100"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              {editing.parent_id != null && (
                <div className="text-xs text-ink-600 bg-amber-50 border border-amber-200 rounded p-2">
                  Subgrupo de: <strong>{grupos.find(g => g.id === editing.parent_id)?.nome || '—'}</strong>
                </div>
              )}
              <div>
                <label className="label">Nome</label>
                <input className="input" autoFocus value={editing.nome || ''}
                  onChange={e => setEditing({ ...editing, nome: e.target.value })}
                  placeholder={editing.parent_id ? 'Ex: Refrigerantes' : 'Ex: Bebidas'} />
              </div>
              {editing.parent_id == null && editing.id && (
                <div>
                  <label className="label">Tipo</label>
                  <select className="input" value={editing.parent_id ?? ''}
                    onChange={e => setEditing({ ...editing, parent_id: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">Grupo (nível principal)</option>
                    {grupos.filter(g => g.id !== editing.id).map(g => (
                      <option key={g.id} value={g.id}>Subgrupo de: {g.nome}</option>
                    ))}
                  </select>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.ativo ?? true}
                  onChange={e => setEditing({ ...editing, ativo: e.target.checked })} />
                Ativo
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.exibir_pdv ?? true}
                  onChange={e => setEditing({ ...editing, exibir_pdv: e.target.checked })} />
                Exibir no PDV
              </label>
            </div>
            <div className="px-5 py-3 bg-ink-50 border-t border-ink-200 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => { setShowForm(false); setEditing(null) }}>Cancelar</button>
              <button tabIndex={0} className="btn-primary" disabled={!editing.nome?.trim() || salvar.isPending}
                onClick={() => salvar.mutate(editing)}>
                {salvar.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
